import React from 'react';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';

import Tooltip from 'ui/tooltip';

import EditingContext from 'store/editingContext';

import './EquipmentPane.scss';
import { D3TooltipCube } from 'tooltips';
import { CubeItemSelect } from 'components/skills/Cube';
import EquipmentMods from './EquipmentMods';
import EquipmentOptimizer from './EquipmentOptimizer';
import EquipmentSection from './Section';
import { cubeSlotValid, cubeTypes } from 'logic/cube';
import scrollIntoView from 'scroll-into-view-if-needed';
import EquipmentItem from './EquipmentItem';
import { SeasonSelect } from 'components/paperdoll/ClassSelect';
import AdUnit from 'AdUnit';

const _L = Locale("ui-equipment.js");

function OptionCheckbox({ name, tip, children }) {
  const dispatch = Store.useDispatch();
  const value = Data.useOption(name);
  const onChange = React.useCallback(e => Data.setOption(name, !!e.target.checked, dispatch), [name, dispatch]);
  return (
    <label>
      <Tooltip tooltip={tip}>
        <input type="checkbox" checked={value} onChange={onChange}/>
      </Tooltip>
      {children}
    </label>
  );
}

const EquipmentOptions = React.memo(() => (
  <div className="d3p-EquipmentOptions">
    <div className="d3p-column">      
      <div><SeasonSelect/></div>
      <OptionCheckbox name="limitStats" tip={_L`Only list stats useful to your class.`}>{_L`Only list class-specific stats`}</OptionCheckbox>
    </div>
    <div className="d3p-column">      
      <OptionCheckbox name="moreWarnings" tip={_L`Show warnings for incomplete items and missing secondary stats.`}>{_L`Show all warnings`}</OptionCheckbox>
      <OptionCheckbox name="hideCrossClass" tip={_L`Do not list items with affixes that do not apply to your class.`}>{_L`Hide items for other classes`}</OptionCheckbox>
      <OptionCheckbox name="hideLegacy" tip={_L`Do not list legacy items.`}>{_L`Hide legacy items`}</OptionCheckbox>
    </div>
  </div>
));

function EquipmentCubeItem({ type }) {
  const id = Store.useProfile(profile => profile.kanai[type]);
  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(id => dispatch.profile.setCubeItem(type, id), [dispatch, type]);

  const selectRef = React.useRef();
  const onEdit = React.useCallback(params => {
    if (params.type === "cube" && type === params.slot) {
      selectRef.current.focus();
      return true;
    }
  }, [type]);
  EditingContext.useHandler(onEdit);

  return (
    <D3TooltipCube id={id}>
      <CubeItemSelect type={type} id={id} onChange={onChange} ref={selectRef}/>
    </D3TooltipCube>
  );
}

const EquipmentCubeSection = React.memo(function EquipmentCubeSection() {
  const setExpanded = EditingContext.useStateSetter("equipmentCubeExpanded");

  const sectionRef = React.useRef();

  const onEdit = React.useCallback(params => {
    if (params.type === "cube") {
      scrollIntoView(sectionRef.current, {scrollMode: "if-needed", block: "center"});
      setExpanded(true);
    }
  }, [setExpanded]);
  EditingContext.useHandler(onEdit);

  const seasonal = Store.useProfile(profile => profile.seasonal);
  return (
    <EquipmentSection id="equipmentCubeExpanded" title={_L`Kanai's Cube`} initialState={true} ref={sectionRef}>
      <div className="d3p-EquipmentCube">
        {Object.keys(cubeTypes).filter(type => cubeSlotValid({seasonal}, type)).map(type => <EquipmentCubeItem key={type} type={type}/>)}
      </div>
    </EquipmentSection>
  );
});

export default React.memo(function EquipmentPane() {
  return <>
    <div className="d3p-TabContent d3p-EquipmentPane">
      <EquipmentOptions/>
      <EquipmentItem/>
      <EquipmentMods/>
      <EquipmentOptimizer/>
      <EquipmentCubeSection/>
    </div>
    <AdUnit id="d3planner-tab-equipment" className="d3p-TabFooter d3planner-tab-ad"/>
  </>;
});
